import { getAttributeConfigAndValue } from "api/helpers/tryGetAttributeValue";
import { Attribute } from "api/types/attribute";
import { SiteSurveyAttribute } from "api/types/siteSurveyAttribute";
import { SiteSurveyConfiguration } from "api/types/siteSurveyConfiguration";
import { SiteSurveyUnit } from "api/types/siteSurveyUnit";
import { Field } from "formik";
import { Accordion } from "react-bootstrap";
import { useUniqueId } from "shared/uniqueId/useUniqueId";
import { getFormikNameForUnitProps } from "./helpers/getFormikNameForUnitProps";
import { useSurveyForm, useUnitsForm } from "./helpers/useSurveyForm";
import { SurveyFormContainer } from "./SurveyFormContainer";

export function BulkEditUnits({ config }: { config: SiteSurveyConfiguration }) {
  const { survey } = useSurveyForm();
  const { addNewUnit, onAttributeValueChange } = useUnitsForm();
  const { uid } = useUniqueId("BreakerSize");

  function handleAddUnit(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    addNewUnit();
  }

  return (
    <SurveyFormContainer className="p-2">
      <Accordion>
        <Accordion.Item eventKey="0">
          <Accordion.Header>Bulk Edit Units</Accordion.Header>
          <Accordion.Body>
            <>
              <table className="table table-sm table-striped table-hover">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>RTU</th>
                    <th>Model</th>
                    <th>Serial</th>
                    <th>Manufacturer</th>
                    <th>Year Built</th>
                    <th>Tonnage</th>
                    <th>Breaker Panel Amperage</th>
                    <th>Wire Size From Panel To Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {survey.units.map((unit, index) => {
                    const manufacturer = getAttributeConfigAndValue(
                      config.unitConfiguration.attributes,
                      unit.attributes,
                      "Manufacturer"
                    );
                    const yearBuilt = getAttributeConfigAndValue(
                      config.unitConfiguration.attributes,
                      unit.attributes,
                      "Year Built"
                    );
                    const tonnage = getAttributeConfigAndValue(
                      config.unitConfiguration.attributes,
                      unit.attributes,
                      "Tonnage"
                    );
                    const amperage = getAttributeConfigAndValue(
                      config.unitConfiguration.attributes,
                      unit.attributes,
                      "Breaker Panel Amperage"
                    );
                    const wireSizeFromPanelToUnit = getAttributeConfigAndValue(
                      config.unitConfiguration.attributes,
                      unit.attributes,
                      "Wire Size From Panel To Unit"
                    );

                    return (
                      <tr key={`BulkEditUnits-${index}-${unit.rtu}`}>
                        <td>{index + 1}</td>
                        <td>
                          <Field
                            className="form-control"
                            id={uid("rtu")}
                            name={getFormikNameForUnitProps(index, "rtu")}
                          />
                        </td>
                        <td>
                          <Field
                            className="form-control"
                            id={uid("modelNumber")}
                            name={getFormikNameForUnitProps(index, "modelNumber")}
                          />
                        </td>
                        <td>
                          <Field
                            className="form-control"
                            id={uid("serial")}
                            name={getFormikNameForUnitProps(index, "serialNumber")}
                          />
                        </td>
                        <td>
                          <UnitAttribute
                            unit={unit}
                            onAttributeValueChange={onAttributeValueChange}
                            attributeConfig={manufacturer.config}
                            attribute={manufacturer.value}
                          />
                        </td>
                        <td>
                          <UnitAttribute
                            unit={unit}
                            onAttributeValueChange={onAttributeValueChange}
                            attributeConfig={yearBuilt.config}
                            attribute={yearBuilt.value}
                          />
                        </td>
                        <td>
                          <UnitAttribute
                            unit={unit}
                            onAttributeValueChange={onAttributeValueChange}
                            attributeConfig={tonnage.config}
                            attribute={tonnage.value}
                          />
                        </td>
                        <td>
                          <UnitAttribute
                            unit={unit}
                            onAttributeValueChange={onAttributeValueChange}
                            attributeConfig={amperage.config}
                            attribute={amperage.value}
                          />
                        </td>
                        <td>
                          <UnitAttribute
                            unit={unit}
                            onAttributeValueChange={onAttributeValueChange}
                            attributeConfig={wireSizeFromPanelToUnit.config}
                            attribute={wireSizeFromPanelToUnit.value}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <button className="btn btn-outline-primary" onClick={handleAddUnit}>
                +
              </button>
            </>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </SurveyFormContainer>
  );
}

type UseUnitsFormType = ReturnType<typeof useUnitsForm>;
interface UnitAttributeType {
  onAttributeValueChange: UseUnitsFormType["onAttributeValueChange"];
  attributeConfig: Attribute;
  unit: SiteSurveyUnit;
  attribute?: SiteSurveyAttribute;
}

function UnitAttribute(props: UnitAttributeType) {
  return (
    <input
      className="form-control"
      value={props.attribute?.attributeValue || ""}
      onChange={(e) => {
        props.onAttributeValueChange(props.unit, props.attributeConfig, e.target.value);
      }}
    />
  );
}
